<template>
  <div :class="clsx('feedbacks-section', { 'animations-disabled': !animationEnabled })" id="feedbacks-slider">
    <IntersectionObserver
      active-class="feedbacks-section__intersector"
      :animation-enabled="animationEnabled"
      :once="true"
      once-class="feedbacks-section__intersector--visible"
      :threshold="0.3"
      @animation-end="observerAnimationEnd"
    >
      <div class="feedbacks-section__title-block">
        <h3 class="feedbacks-section__title" v-html="getTitleBlock('promo', 'feedbacks', 'title')"></h3>
        <p class="feedbacks-section__description" v-html="getTitleBlock('promo', 'feedbacks', 'description')"></p>
      </div>

      <CarouselSliderBox
        v-if="isFeedbacksReady"
        id="feedbacks-slider"
        class="feedbacks-slider"
        orientation="horizontal"
        :slides-to-show="1"
        :gap="windowWidth <= 720 ? '10px' : 'auto'"
        :extra-slide-size="windowWidth <= 720 ? '5%' : '15%'"
        :infinite-loop="true"
        :autoplay="sliderAutoplay"
        :autoplay-interval="10000"
        :show-slider-controls="false"
        slider-class="feedbacks-slider__pagination"
        :initial-slide="1"
      >
        <FeedbackSlide
          class="feedbacks-slider__slide"
          v-for="(feedback, index) in getFeedbacks"
          :key="index"
          :feedback="feedback"
        />
      </CarouselSliderBox>
    </IntersectionObserver>
  </div>
</template>

<script>
import clsx from 'clsx';
import { mapActions, mapGetters } from 'vuex';
import FeedbackSlide from './feedback-slide';
import { textBlock } from '@/entities/text-block';
import { IntersectionObserver } from '@/shared/ui/intersection-observer';
import { CarouselSliderBox } from '@/shared/ui/slider';

export default {
  props: {
    animationEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      sliderAutoplay: false,
    };
  },
  created() {
    this.fetchFeedbacks();
  },
  mixins: [ textBlock ],
  components: { CarouselSliderBox, FeedbackSlide, IntersectionObserver },
  computed: {
    ...mapGetters('layoutModule', ['windowWidth']),
    ...mapGetters('promoFeedbacksModule', ['isFeedbacksReady', 'getFeedbacks']),
  },
  methods: {
    ...mapActions('promoFeedbacksModule', ['fetchFeedbacks']),
    clsx,
    observerAnimationEnd() {
      this.sliderAutoplay = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.feedbacks-slider {
  padding-bottom: 70px;

  &__slide {
    transform: scale(0.5);
    opacity: 0;
    animation: feedback-slide-animation 0.5s linear forwards;
    animation-delay: 1s;
    animation-play-state: paused;
  }

  ::v-deep .carousel-slider__track {
    align-items: flex-end !important;
  }
}

.feedbacks-section {
  position: relative;
  padding-bottom: 48px;

  &__intersector {
    &--visible {
      .feedbacks-section__title,
      .feedbacks-section__description,
      .feedbacks-slider__slide {
        animation-play-state: running;
      }
    }
  }

  &__title-block {
    text-align: center;
    padding: 70px 0 55px;
  }

  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 52px;
    font-weight: 500;
    color: #313437;
    text-align: center;
    margin-bottom: 16px;
    transform: translateY(100%);
    opacity: 0;
    animation: slideup-text-animation 0.5s linear forwards;
    animation-play-state: paused;
  }

  &__description {
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #494E53;
    transform: translateY(100%);
    opacity: 0;
    animation: slideup-text-animation 0.5s linear forwards;
    animation-delay: 0.5s;
    animation-play-state: paused;
  }
}

.animations-disabled,
.animations-disabled * {
  animation: none !important;
  animation-duration: 0s !important;
  animation-delay: 0s !important;
  transition: none !important;
}

.animations-disabled {
  .feedbacks-slider__slide {
    transform: scale(1);
    opacity: 1;
  }

  .feedbacks-section {
    &__title, &__description {
      opacity: 1;
      clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
      transform: translateY(0);
    }
  }
}

@keyframes slideup-text-animation {
  0% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 0%, 0 0%);
    transform: translateY(100%);
  }
  20% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
    transform: translateY(80%);
  }
  40% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 40%, 0 40%);
    transform: translateY(60%);
  }
  60% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
    transform: translateY(40%);
  }
  80% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 80%, 0 80%);
    transform: translateY(20%);
  }
  100% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    transform: translateY(0);
  }
}

@keyframes feedback-slide-animation {
  from {
    transform: scale(0.5);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
